import React, { useCallback, useEffect, useState } from "react";
import Spinner from "@atlaskit/spinner";
import Checkbox from "@atlaskit/checkbox";
import EditorCloseIcon from "@atlaskit/icon/glyph/editor/close";
import { PickerOptionTypes } from "./option-types";
import { StyledLabel } from "./StyledHelpers";
import { useTheme } from "styled-components";
import {
  FieldContainer,
  Label,
  LabelIconContainer,
  SmallIconContainer,
  ItalicInfo,
  List,
  OptionLabel,
  OptionWrapper,
  SelectableUserWrapper,
  SpinnerContainer,
  WideButton,
} from "./styled";
import { useStructureStore } from "../store/structure-store";
import {
  getGroupMembersById,
  getMembersOfGroup,
} from "../../../../../../../api/people-api";

function UsersFromGroup({ groupData, onSelect, onCancel, selectedUser }) {
  const [isLoading, setIsLoading] = useState(false);
  const [usersInGroup, setUsersInGroup] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const theme = useTheme();
  const { checkIsUserInStructure } = useStructureStore((state) => ({
    checkIsUserInStructure: state.checkIsUserInStructure,
  }));

  const buildUserObject = useCallback(
    (user) => ({
      renderLabel: () => (
        <StyledLabel type={PickerOptionTypes.USER} data={user} />
      ),
      value: user.accountId,
      type: PickerOptionTypes.USER,
      name: user.displayName,
      isInStructure: checkIsUserInStructure(user.accountId),
    }),
    [checkIsUserInStructure]
  );

  useEffect(() => {
    if (groupData?.id) {
      setIsLoading(true);
      (async () => {
        try {
          const response = await getGroupMembersById(groupData.id);
          const groupUsers = JSON.parse(response.body);

          if (groupUsers?.results) {
            const builtUsers = groupUsers.results
              .filter((user) => user.accountType === "atlassian")
              .map((user) => buildUserObject(user));
            setUsersInGroup([...builtUsers]);
            setSelectedUsers([
              ...builtUsers
                .filter((user) => !user.isInStructure)
                .map((user) => user.value),
            ]);
          } else {
            setUsersInGroup([]);
            setSelectedUsers([]);
          }
        } catch (error) {
          setUsersInGroup([]);
          setSelectedUsers([]);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [groupData, buildUserObject]);

  const handleChangeInCheckbox = (isChecked, userId) => {
    if (isChecked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(
        selectedUsers.filter((userAccountId) => userAccountId !== userId)
      );
    }
  };

  return (
    <FieldContainer>
      <LabelIconContainer>
        <Label>
          Select users from {groupData?.name}
          {selectedUser?.name ? ` below ${selectedUser.name}` : ""}
        </Label>
        <SmallIconContainer
          onClick={() => onCancel()}
          style={{ marginBottom: "5px" }}
        >
          <EditorCloseIcon primaryColor="#6b778c" />
        </SmallIconContainer>
      </LabelIconContainer>
      {isLoading && (
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      )}
      {!isLoading && (
        <>
          {usersInGroup?.length ? (
            <>
              <List>
                {usersInGroup.map(({ renderLabel, value, isInStructure }) => (
                  <SelectableUserWrapper key={value}>
                    {renderLabel()}
                    <OptionWrapper style={{ gridTemplateColumns: "auto auto" }}>
                      <ItalicInfo>
                        {isInStructure && "Already added"}
                      </ItalicInfo>
                      <Checkbox
                        isChecked={
                          isInStructure ||
                          selectedUsers.find(
                            (userAccountId) => userAccountId === value
                          )
                        }
                        onChange={({ target: { checked } }) =>
                          handleChangeInCheckbox(checked, value)
                        }
                        isDisabled={isInStructure}
                      />
                    </OptionWrapper>
                  </SelectableUserWrapper>
                ))}
              </List>
              <WideButton
                style={{ marginTop: "24px", marginBottom: "20px" }}
                onClick={() => {
                  if (selectedUsers?.length) onSelect(selectedUsers);
                }}
              >
                Add selected
              </WideButton>
            </>
          ) : (
            <OptionLabel>No users found in group.</OptionLabel>
          )}
        </>
      )}
    </FieldContainer>
  );
}

export default UsersFromGroup;
